import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";
import { openAssistantSurface } from "../../features/toolExperience/assistantSurface";
import styles from "./AssistantLauncher.module.css";

const POSITION_KEY = "lazysem:assistant-launcher-position:v1";
type Dock = { side: "left" | "right"; y: number };
type Point = { x: number; y: number };
type Bounds = { left: number; right: number; top: number; bottom: number };
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

function savedDock(): Dock {
  try {
    const value = JSON.parse(localStorage.getItem(POSITION_KEY) || "null");
    if (
      value &&
      ["left", "right"].includes(value.side) &&
      typeof value.y === "number" &&
      Number.isFinite(value.y) &&
      value.y >= 0 &&
      value.y <= 1
    )
      return value;
  } catch {
    /* Storage is optional; movement still works in this session. */
  }
  return { side: "right", y: 1 };
}

export default function AssistantLauncher({
  hidden,
  unread,
}: {
  hidden: boolean;
  unread: boolean;
}) {
  const [dock, setDock] = useState<Dock>(savedDock);
  const [point, setPoint] = useState<Point | null>(null);
  const [dragging, setDragging] = useState(false);
  const area = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const bounds = useRef<Bounds>({ left: 12, right: 12, top: 12, bottom: 12 });
  const drag = useRef<{
    id: number;
    start: Point;
    origin: Point;
    position: Point;
    moved: boolean;
  } | null>(null);
  const suppressClick = useRef(false);
  const helpId = useId();

  useLayoutEffect(() => {
    const element = area.current;
    if (!element) return;
    const measure = () => {
      const style = getComputedStyle(element);
      const left = parseFloat(style.paddingLeft),
        top = parseFloat(style.paddingTop);
      const next = {
        left,
        top,
        right: Math.max(
          left,
          element.clientWidth - parseFloat(style.paddingRight) - 48,
        ),
        bottom: Math.max(
          top,
          element.clientHeight - parseFloat(style.paddingBottom) - 48,
        ),
      };
      bounds.current = next;
      if (drag.current?.moved) suppressClick.current = true;
      drag.current = null;
      setDragging(false);
      setPoint({
        x: dock.side === "left" ? next.left : next.right,
        y: next.top + dock.y * (next.bottom - next.top),
      });
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [dock]);

  const save = (next: Dock) => {
    setDock(next);
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify(next));
    } catch {
      /* Keep the current position when storage is unavailable. */
    }
  };
  const finish = (
    event: PointerEvent<HTMLButtonElement>,
    cancelled = false,
  ) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (cancelled) {
      suppressClick.current = false;
      const b = bounds.current;
      setPoint({
        x: dock.side === "left" ? b.left : b.right,
        y: b.top + dock.y * (b.bottom - b.top),
      });
    } else if (current.moved) {
      const b = bounds.current;
      save({
        side: current.position.x < (b.left + b.right) / 2 ? "left" : "right",
        y:
          b.bottom === b.top
            ? 0
            : (current.position.y - b.top) / (b.bottom - b.top),
      });
    }
  };

  return (
    <div ref={area} className={styles.area}>
      <button
        ref={button}
        type="button"
        className={styles.launcher}
        hidden={hidden}
        style={{
          left: point?.x ?? 12,
          top: point?.y ?? 12,
          visibility: point ? "visible" : "hidden",
        }}
        data-dragging={dragging || undefined}
        aria-label="Open assistant"
        aria-describedby={helpId}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown"
        title={
          unread
            ? "Ask assistant · New reply — drag to move"
            : "Ask assistant — drag to move"
        }
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0 || !point) return;
          suppressClick.current = false;
          drag.current = {
            id: event.pointerId,
            start: { x: event.clientX, y: event.clientY },
            origin: point,
            position: point,
            moved: false,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const current = drag.current;
          if (!current || current.id !== event.pointerId) return;
          const dx = event.clientX - current.start.x,
            dy = event.clientY - current.start.y;
          if (!current.moved && Math.hypot(dx, dy) < 6) return;
          current.moved = true;
          suppressClick.current = true;
          setDragging(true);
          const b = bounds.current;
          current.position = {
            x: clamp(current.origin.x + dx, b.left, b.right),
            y: clamp(current.origin.y + dy, b.top, b.bottom),
          };
          setPoint(current.position);
        }}
        onPointerUp={(event) => {
          const tapped =
            drag.current?.id === event.pointerId &&
            !drag.current.moved &&
            event.pointerType !== "mouse";
          finish(event);
          if (tapped) {
            // Touch browsers can omit the synthesized click just after a drag.
            suppressClick.current = true;
            event.preventDefault();
            event.currentTarget.focus();
            openAssistantSurface();
          }
        }}
        onPointerCancel={(event) => finish(event, true)}
        onLostPointerCapture={(event) => finish(event, true)}
        onKeyDown={(event) => {
          if (
            !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
              event.key,
            ) ||
            !point
          )
            return;
          event.preventDefault();
          const b = bounds.current;
          save({
            side:
              event.key === "ArrowLeft"
                ? "left"
                : event.key === "ArrowRight"
                  ? "right"
                  : dock.side,
            y:
              event.key === "ArrowUp" || event.key === "ArrowDown"
                ? clamp(
                    dock.y +
                      (event.key === "ArrowUp" ? -48 : 48) /
                        Math.max(1, b.bottom - b.top),
                    0,
                    1,
                  )
                : dock.y,
          });
        }}
        onClick={(event) => {
          if (suppressClick.current && event.detail !== 0) {
            suppressClick.current = false;
            return;
          }
          suppressClick.current = false;
          event.currentTarget.focus();
          openAssistantSurface();
        }}
      >
        <img
          src="/LazySEMCat.svg"
          alt=""
          aria-hidden="true"
          draggable={false}
        />
        {unread && <span className={styles.unread} aria-hidden="true" />}
      </button>
      <span id={helpId} className={styles.help}>
        Drag to move, or use the arrow keys while focused. Press Enter to open
        the assistant.{unread ? " A new reply is available." : ""}
      </span>
    </div>
  );
}
